import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Send, RotateCcw, SlidersHorizontal, Check, AlertTriangle, Wrench } from 'lucide-react';
// ─── TYPES ──────────────────────────────────────────────────────────────────
interface CategoryWeight {
  id: string;
  label: string;
  icon: string;
  weight: number;
  defaultWeight: number;
}
interface RiskTuningPanelProps {
  isOpen: boolean;
  onClose: () => void;
  categoryWeights: CategoryWeight[];
  onWeightsChange: (weights: Record<string, number>) => void;
  onOpenCustomization?: () => void;
}
interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  proposedWeights?: Record<string, number>;
  applied?: boolean;
}
// ─── PRESETS ────────────────────────────────────────────────────────────────
const PRESETS = [{
  label: 'High-Rise Construction',
  prompt: 'We are doing high-rise new construction. Fall protection and wind are huge concerns.',
  weights: {
    safetyPlanning: 18,
    workforce: 12,
    environment: 18,
    incidents: 12,
    inspections: 12,
    documentation: 8,
    subcontractor: 8,
    equipment: 8,
    regulatory: 4
  },
  explanation: "For high-rise work, I've increased Environment to 18% (wind, weather exposure) and Equipment to 8% (crane/hoist criticality). Safety Planning stays high at 18% since PTP quality is essential for elevated work. I've slightly reduced Workforce and Incidents to compensate."
}, {
  label: 'Interior Renovation',
  prompt: "We mostly do interior renovation. Weather shouldn't matter as much.",
  weights: {
    safetyPlanning: 22,
    workforce: 18,
    environment: 4,
    incidents: 15,
    inspections: 14,
    documentation: 12,
    subcontractor: 8,
    equipment: 4,
    regulatory: 3
  },
  explanation: "For interior work, I've reduced Environment to just 4% since weather has minimal impact. I've redistributed that weight to Safety Planning (22%), Workforce (18%), and Documentation (12%) — housekeeping, worker fatigue, and permit compliance are your primary risk drivers indoors."
}, {
  label: 'High Turnover / New Crews',
  prompt: 'We have a lot of new workers and high turnover right now.',
  weights: {
    safetyPlanning: 15,
    workforce: 25,
    environment: 8,
    incidents: 12,
    inspections: 10,
    documentation: 8,
    subcontractor: 14,
    equipment: 4,
    regulatory: 4
  },
  explanation: "I've shifted primary focus to Workforce (25%) and Subcontractor Performance (14%). The model will now be much more sensitive to new worker ratios, crew stability, and sub compliance. Safety Planning drops slightly since new crews often have less control over PTP quality."
}, {
  label: 'Post-Incident Focus',
  prompt: 'We just had a serious incident and need to tighten everything up.',
  weights: {
    safetyPlanning: 15,
    workforce: 12,
    environment: 8,
    incidents: 22,
    inspections: 18,
    documentation: 10,
    subcontractor: 6,
    equipment: 4,
    regulatory: 5
  },
  explanation: "After a serious incident, I've heavily weighted Incidents & Loop Closure (22%) and Inspections (18%). This ensures the model flags any open corrective actions, recurring patterns, or inspection gaps immediately. Regulatory is also bumped to 5% for OSHA citation awareness."
}];
// ─── COMPONENT ──────────────────────────────────────────────────────────────
export function RiskTuningPanel({
  isOpen,
  onClose,
  categoryWeights,
  onWeightsChange,
  onOpenCustomization
}: RiskTuningPanelProps) {
  const [activeTab, setActiveTab] = useState<'sliders' | 'chat'>('sliders');
  const [localWeights, setLocalWeights] = useState<Record<string, number>>({});
  const [messages, setMessages] = useState<Message[]>([{
    id: 'welcome',
    role: 'assistant',
    text: "Hi! I can help you tune your risk model to match your specific site conditions. Tell me about your project type, or pick a preset below. I'll suggest weight adjustments that keep everything balanced at 100%."
  }]);
  const [query, setQuery] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Sync local weights from props
  useEffect(() => {
    const w: Record<string, number> = {};
    categoryWeights.forEach(c => {
      w[c.id] = c.weight;
    });
    setLocalWeights(w);
  }, [categoryWeights]);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, isTyping]);
  const total = Object.values(localWeights).reduce((s, v) => s + v, 0);
  // Reset confirm dialogs when total drifts from 100%
  useEffect(() => {
    if (total !== 100) {
      setShowConfirm(false);
    }
  }, [total]);
  // Reset confirm dialogs on tab switch
  useEffect(() => {
    setShowConfirm(false);
    setShowResetConfirm(false);
  }, [activeTab]);
  const isModified = categoryWeights.some(c => (localWeights[c.id] ?? c.weight) !== c.defaultWeight);
  const handleSliderChange = (catId: string, newValue: number) => {
    setLocalWeights(prev => ({
      ...prev,
      [catId]: newValue
    }));
  };
  const handleAutoNormalize = () => {
    setLocalWeights(prev => {
      const keys = Object.keys(prev);
      const currentTotal = keys.reduce((s, k) => s + prev[k], 0);
      if (currentTotal === 100 || currentTotal === 0) return prev;
      const next: Record<string, number> = {};
      let allocated = 0;
      keys.forEach((key, idx) => {
        if (idx === keys.length - 1) {
          next[key] = Math.max(0, 100 - allocated);
        } else {
          const scaled = Math.max(0, Math.floor(prev[key] / currentTotal * 100));
          next[key] = scaled;
          allocated += scaled;
        }
      });
      return next;
    });
  };
  const handleReset = () => {
    const w: Record<string, number> = {};
    categoryWeights.forEach(c => {
      w[c.id] = c.defaultWeight;
    });
    setLocalWeights(w);
  };
  const handleApplyWeights = (weights?: Record<string, number>) => {
    const w = weights || localWeights;
    // Final safety check: only apply if total is 100%
    const t = Object.values(w).reduce((s, v) => s + v, 0);
    if (t !== 100) return;
    onWeightsChange(w);
  };
  const handleSend = (text: string, preset?: (typeof PRESETS)[0]) => {
    if (!text.trim()) return;
    const newUserMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      text
    };
    setMessages(prev => [...prev, newUserMsg]);
    setQuery('');
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      let responseText = '';
      let newWeights: Record<string, number> = {
        ...localWeights
      };
      if (preset) {
        responseText = preset.explanation;
        newWeights = preset.weights;
      } else {
        const lowerText = text.toLowerCase();
        if (lowerText.includes('weather') || lowerText.includes('rain') || lowerText.includes('wind') || lowerText.includes('outdoor')) {
          responseText = "I understand weather and environmental conditions are key for you. I've increased Environment to 16% and reduced Documentation and Equipment to compensate. The model will now be more sensitive to weather-related risks.";
          newWeights = {
            ...localWeights,
            environment: 16,
            documentation: Math.max(3, (localWeights.documentation || 10) - 4),
            equipment: Math.max(2, (localWeights.equipment || 5) - 2)
          };
        } else if (lowerText.includes('paperwork') || lowerText.includes('ptp') || lowerText.includes('jha') || lowerText.includes('document')) {
          responseText = "Got it — documentation compliance is your leading indicator. I've increased Safety Planning to 24% and Documentation to 14%. The model will flag sites faster if PTPs are missing or generic.";
          newWeights = {
            ...localWeights,
            safetyPlanning: 24,
            documentation: 14,
            environment: Math.max(3, (localWeights.environment || 10) - 4)
          };
        } else if (lowerText.includes('worker') || lowerText.includes('crew') || lowerText.includes('turnover') || lowerText.includes('fatigue')) {
          responseText = "I've shifted focus to Workforce (22%) and Subcontractor Performance (12%). The model will be much more sensitive to crew stability, new worker ratios, and sub compliance.";
          newWeights = {
            ...localWeights,
            workforce: 22,
            subcontractor: 12,
            environment: Math.max(3, (localWeights.environment || 10) - 4)
          };
        } else if (lowerText.includes('incident') || lowerText.includes('accident') || lowerText.includes('injury')) {
          responseText = "After incidents, loop closure is critical. I've increased Incidents & Loop Closure to 20% and Inspections to 16%. Open corrective actions and reinspection gaps will be flagged more aggressively.";
          newWeights = {
            ...localWeights,
            incidents: 20,
            inspections: 16,
            subcontractor: Math.max(3, (localWeights.subcontractor || 8) - 4)
          };
        } else {
          responseText = "Based on what you shared, I've made moderate adjustments to prioritize Safety Planning and Workforce, which are typically the strongest leading indicators. Feel free to fine-tune further or try a preset.";
          newWeights = {
            ...localWeights,
            safetyPlanning: 22,
            workforce: 18,
            environment: 8,
            incidents: 14,
            inspections: 12,
            documentation: 10,
            subcontractor: 7,
            equipment: 5,
            regulatory: 4
          };
        }
        // Normalize
        const t = Object.values(newWeights).reduce((s, v) => s + v, 0);
        if (t !== 100) {
          const keys = Object.keys(newWeights);
          let remaining = 100;
          keys.forEach((key, idx) => {
            if (idx === keys.length - 1) {
              newWeights[key] = remaining;
            } else {
              const val = Math.round(newWeights[key] / t * 100);
              newWeights[key] = val;
              remaining -= val;
            }
          });
        }
      }
      const newAiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        text: responseText,
        proposedWeights: newWeights,
        applied: false
      };
      setMessages(prev => [...prev, newAiMsg]);
    }, 1200);
  };
  const handleApplyProposed = (msgId: string, weights: Record<string, number>) => {
    // Normalize proposed weights to guarantee 100% sum
    const t = Object.values(weights).reduce((s, v) => s + v, 0);
    let normalizedWeights = {
      ...weights
    };
    if (t !== 100 && t > 0) {
      const keys = Object.keys(normalizedWeights);
      let remaining = 100;
      keys.forEach((key, idx) => {
        if (idx === keys.length - 1) {
          normalizedWeights[key] = remaining;
        } else {
          const val = Math.max(0, Math.round(normalizedWeights[key] / t * 100));
          normalizedWeights[key] = val;
          remaining -= val;
        }
      });
    }
    setLocalWeights(normalizedWeights);
    onWeightsChange(normalizedWeights);
    setMessages(prev => prev.map(m => m.id === msgId ? {
      ...m,
      applied: true
    } : m));
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-2655">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex justify-end" data-id="element-2656">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/30" onClick={onClose} data-id="element-2657" />

        {/* Panel */}
        <motion.div initial={{
        x: 420
      }} animate={{
        x: 0
      }} exit={{
        x: 420
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="relative w-[420px] h-full bg-white shadow-2xl flex flex-col" data-id="element-2658">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200" data-id="element-2659">
            <div data-id="element-2660">
              <h2 className="text-base font-bold text-gray-900" data-id="element-2661">
                Tune Risk Model
              </h2>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-2662">
                Adjust how risk factors influence the final score
              </p>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2663">
              <X size={18} data-id="element-2664" />
            </button>
          </div>

          {/* Tab switcher */}
          <div className="flex items-center gap-1 px-5 py-3 border-b border-gray-100 bg-gray-50" data-id="element-2665">
            <button onClick={() => setActiveTab('sliders')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${activeTab === 'sliders' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-2666">
              <SlidersHorizontal size={13} data-id="element-2667" />
              Manual
            </button>
            {onOpenCustomization && <button onClick={onOpenCustomization} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-gray-500 hover:text-gray-700 transition-all" data-id="element-2668">
                <Wrench size={13} data-id="element-2669" />
                Customize
              </button>}
            <button onClick={() => setActiveTab('chat')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${activeTab === 'chat' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-2670">
              <Sparkles size={13} data-id="element-2671" />
              AI Advisor
              <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full leading-none" data-id="element-2672">
                Beta
              </span>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden flex flex-col" data-id="element-2673">
            {activeTab === 'sliders' /* ── SLIDERS TAB ── */ ? <div className="flex-1 flex flex-col overflow-hidden" data-id="element-2674">
                {/* Total indicator — FIXED above scroll area */}
                <div className={`px-5 py-3 border-b ${total === 100 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-id="element-2675">
                  <div className="flex items-center justify-between" data-id="element-2676">
                    <div className="flex items-center gap-3" data-id="element-2677">
                      <span className="text-xs font-bold text-gray-600 uppercase tracking-wider" data-id="element-2678">
                        Total
                      </span>
                      <span className={`text-2xl font-black ${total === 100 ? 'text-emerald-700' : 'text-red-700'}`} data-id="element-2679">
                        {total}%
                      </span>
                      {total === 100 && <span className="flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full" data-id="element-2680">
                          <Check size={12} data-id="element-2681" />
                          Balanced
                        </span>}
                    </div>
                    <div className="flex items-center gap-2" data-id="element-2682">
                      {total !== 100 && <button onClick={handleAutoNormalize} className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700 px-2.5 py-1.5 rounded-lg bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition-colors" data-id="element-2683">
                          <Sparkles size={11} data-id="element-2684" />
                          Auto Normalize
                        </button>}
                      <button onClick={() => setShowResetConfirm(true)} className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-700 px-2.5 py-1.5 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors" data-id="element-2685">
                        <RotateCcw size={11} data-id="element-2686" />
                        Reset
                      </button>
                    </div>
                  </div>
                  {/* Total bar */}
                  <div className="mt-2.5 h-2 bg-white/60 rounded-full overflow-hidden" data-id="element-2687">
                    <div className={`h-full rounded-full transition-all duration-300 ${total === 100 ? 'bg-emerald-500' : 'bg-red-500'}`} style={{
                  width: `${Math.min(100, total)}%`
                }} data-id="element-2688" />
                  </div>
                </div>

                {/* Category sliders — scrollable */}
                <div className="flex-1 overflow-y-auto px-5 py-3 space-y-4" data-id="element-2689">
                  {categoryWeights.map(cat => {
                const value = localWeights[cat.id] ?? cat.weight;
                const changed = value !== cat.defaultWeight;
                const isExtreme = value > 0 && value < 5;
                const isHeavy = value > 50;
                return <div key={cat.id} data-id="element-2690">
                        <div className="flex items-center justify-between mb-1.5" data-id="element-2691">
                          <div className="flex items-center gap-2" data-id="element-2692">
                            <span className="text-sm" data-id="element-2693">{cat.icon}</span>
                            <span className="text-xs font-bold text-gray-800" data-id="element-2694">
                              {cat.label}
                            </span>
                            {changed && !isExtreme && !isHeavy && <span className="text-[9px] font-semibold text-amber-600 bg-amber-50 px-1 py-0.5 rounded" data-id="element-2695">
                                modified
                              </span>}
                            {isExtreme && <span className="text-[9px] font-semibold text-red-600 bg-red-50 px-1 py-0.5 rounded flex items-center gap-0.5" data-id="element-2696">
                                <AlertTriangle size={9} data-id="element-2697" />
                                very low
                              </span>}
                            {isHeavy && <span className="text-[9px] font-semibold text-red-600 bg-red-50 px-1 py-0.5 rounded flex items-center gap-0.5" data-id="element-2698">
                                <AlertTriangle size={9} data-id="element-2699" />
                                very high
                              </span>}
                          </div>
                          <div className="flex items-center gap-2" data-id="element-2700">
                            <span className={`text-sm font-black w-8 text-right ${isExtreme || isHeavy ? 'text-red-600' : 'text-gray-900'}`} data-id="element-2701">
                              {value}%
                            </span>
                            {changed && <span className="text-[10px] text-gray-400 line-through" data-id="element-2702">
                                {cat.defaultWeight}%
                              </span>}
                          </div>
                        </div>
                        <input type="range" min={0} max={65} value={value} onChange={e => handleSliderChange(cat.id, parseInt(e.target.value))} className={`w-full h-1.5 rounded-full appearance-none cursor-pointer ${isExtreme || isHeavy ? 'bg-red-200 accent-red-600' : 'bg-gray-200 accent-indigo-600'}`} data-id="element-2703" />
                        {isExtreme && <p className="text-[10px] text-red-600 mt-1" data-id="element-2704">
                            ⚠ Below 5% may cause blind spots in risk detection
                          </p>}
                        {isHeavy && <p className="text-[10px] text-red-600 mt-1" data-id="element-2705">
                            ⚠ Above 50% may over-index on a single factor
                          </p>}
                      </div>;
              })}
                </div>
              </div> /* ── CHAT TAB ── */ : <div className="flex-1 flex flex-col overflow-hidden" data-id="element-2706">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-id="element-2707">
                  {messages.map(msg => <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-2708">
                      <div className={`max-w-[90%] p-3 rounded-2xl text-sm ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-tr-sm' : 'bg-white border border-gray-200 text-gray-800 rounded-tl-sm shadow-sm'}`} data-id="element-2709">
                        {msg.role === 'assistant' && <div className="flex items-center gap-1.5 mb-1.5 text-indigo-600" data-id="element-2710">
                            <Sparkles size={11} data-id="element-2711" />
                            <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-2712">
                              Bilton AI Advisor
                            </span>
                          </div>}
                        <p className="leading-relaxed" data-id="element-2713">{msg.text}</p>

                        {/* Proposed weights */}
                        {msg.proposedWeights && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-2714">
                            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-2715">
                              Proposed Weights
                            </p>
                            <div className="space-y-1" data-id="element-2716">
                              {categoryWeights.map(cat => {
                        const proposed = msg.proposedWeights![cat.id] ?? 0;
                        const current = localWeights[cat.id] ?? cat.weight;
                        const diff = proposed - current;
                        return <div key={cat.id} className="flex items-center gap-2 text-xs" data-id="element-2717">
                                    <span className="w-4 text-center" data-id="element-2718">
                                      {cat.icon}
                                    </span>
                                    <span className="flex-1 text-gray-600 truncate" data-id="element-2719">
                                      {cat.label}
                                    </span>
                                    <span className="font-bold text-gray-800 w-7 text-right" data-id="element-2720">
                                      {proposed}%
                                    </span>
                                    {diff !== 0 && <span className={`text-[10px] font-semibold w-8 text-right ${diff > 0 ? 'text-emerald-600' : 'text-red-500'}`} data-id="element-2721">
                                        {diff > 0 ? '+' : ''}
                                        {diff}
                                      </span>}
                                  </div>;
                      })}
                            </div>
                            {!msg.applied ? <button onClick={() => handleApplyProposed(msg.id, msg.proposedWeights!)} className="mt-3 w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition-colors" data-id="element-2722">
                                Apply These Weights
                              </button> : <div className="mt-3 flex items-center justify-center gap-1.5 py-2 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-lg" data-id="element-2723">
                                <Check size={13} data-id="element-2724" />
                                Applied
                              </div>}
                          </div>}
                      </div>
                    </div>)}

                  {isTyping && <div className="flex justify-start" data-id="element-2725">
                      <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm" data-id="element-2726">
                        <div className="flex items-center gap-1.5 text-indigo-600 mb-1" data-id="element-2727">
                          <Sparkles size={11} data-id="element-2728" />
                          <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-2729">
                            Thinking...
                          </span>
                        </div>
                        <div className="flex gap-1" data-id="element-2730">
                          <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{
                      animationDelay: '0ms'
                    }} data-id="element-2731" />
                          <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{
                      animationDelay: '150ms'
                    }} data-id="element-2732" />
                          <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{
                      animationDelay: '300ms'
                    }} data-id="element-2733" />
                        </div>
                      </div>
                    </div>}
                  <div ref={messagesEndRef} data-id="element-2734" />
                </div>

                {/* Presets */}
                <div className="px-4 py-2 border-t border-gray-100 bg-gray-50" data-id="element-2735">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-2736">
                    Quick Presets
                  </p>
                  <div className="flex flex-wrap gap-1.5" data-id="element-2737">
                    {PRESETS.map(preset => <button key={preset.label} onClick={() => handleSend(preset.prompt, preset)} className="px-2.5 py-1 text-[11px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors" data-id="element-2738">
                        {preset.label}
                      </button>)}
                  </div>
                </div>

                {/* Input */}
                <div className="px-4 py-3 border-t border-gray-200 bg-white" data-id="element-2739">
                  <div className="flex items-center gap-2" data-id="element-2740">
                    <input type="text" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend(query)} placeholder="Describe your project or concerns..." className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" data-id="element-2741" />
                    <button onClick={() => handleSend(query)} disabled={!query.trim() || isTyping} className="p-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white rounded-lg transition-colors" data-id="element-2742">
                      <Send size={14} data-id="element-2743" />
                    </button>
                  </div>
                </div>
              </div>}
          </div>

          {/* Footer — Apply + Restore (sliders tab only) */}
          {activeTab === 'sliders' && <div className="px-5 py-4 border-t border-gray-200 bg-white space-y-2" data-id="element-2744">
              <AnimatePresence mode="wait" data-id="element-2745">
                {showResetConfirm ? <motion.div key="reset-confirm" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} className="space-y-2" data-id="element-2746">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-id="element-2747">
                      <p className="text-sm font-bold text-amber-900 mb-1" data-id="element-2748">
                        Restore default weights?
                      </p>
                      <p className="text-xs text-amber-700" data-id="element-2749">
                        This will discard all your custom adjustments and reset
                        every category to its original weight. Changes take
                        effect immediately.
                      </p>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-2750">
                      <button onClick={() => setShowResetConfirm(false)} className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors" data-id="element-2751">
                        Cancel
                      </button>
                      <button onClick={() => {
                  handleReset();
                  onWeightsChange(Object.fromEntries(categoryWeights.map(c => [c.id, c.defaultWeight])));
                  setShowResetConfirm(false);
                }} className="flex-1 py-2.5 rounded-lg text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 transition-colors flex items-center justify-center gap-1.5" data-id="element-2752">
                        <RotateCcw size={13} data-id="element-2753" />
                        Restore Defaults
                      </button>
                    </div>
                  </motion.div> : !showConfirm ? <motion.div key="buttons" initial={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="space-y-2" data-id="element-2754">
                    <button onClick={() => setShowConfirm(true)} disabled={total !== 100} className={`w-full py-2.5 rounded-lg text-sm font-bold transition-colors ${total === 100 ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-2755">
                      {total !== 100 ? `Weights must sum to 100% (currently ${total}%)` : 'Apply Weights'}
                    </button>
                    {isModified && <button onClick={() => setShowResetConfirm(true)} className="w-full py-2 rounded-lg text-xs font-semibold text-gray-500 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-colors flex items-center justify-center gap-1.5" data-id="element-2756">
                        <RotateCcw size={12} data-id="element-2757" />
                        Restore Default Weights
                      </button>}
                  </motion.div> : <motion.div key="confirm" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} className="space-y-2" data-id="element-2758">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-id="element-2759">
                      <p className="text-sm font-bold text-amber-900 mb-1" data-id="element-2760">
                        Confirm weight changes?
                      </p>
                      <p className="text-xs text-amber-700" data-id="element-2761">
                        This will update how all risk scores are calculated
                        across the matrix. Changes take effect immediately.
                      </p>
                      {(() => {
                  const extremes = categoryWeights.filter(c => {
                    const v = localWeights[c.id] ?? c.weight;
                    return v > 0 && v < 5 || v > 50;
                  });
                  if (extremes.length === 0) return null;
                  return <div className="mt-2 pt-2 border-t border-amber-200" data-id="element-2762">
                            <p className="text-[10px] font-bold text-red-700 uppercase tracking-wider mb-1" data-id="element-2763">
                              ⚠ Extreme values detected
                            </p>
                            {extremes.map(c => {
                      const v = localWeights[c.id] ?? c.weight;
                      const isLow = v > 0 && v < 5;
                      return <p key={c.id} className="text-[11px] text-red-700" data-id="element-2764">
                                  {c.icon} {c.label}: {v}% —{' '}
                                  {isLow ? 'may cause blind spots' : 'may over-index'}
                                </p>;
                    })}
                          </div>;
                })()}
                    </div>
                    <div className="flex items-center gap-2" data-id="element-2765">
                      <button onClick={() => setShowConfirm(false)} className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors" data-id="element-2766">
                        Cancel
                      </button>
                      <button onClick={() => {
                  handleApplyWeights();
                  setShowConfirm(false);
                  onClose();
                }} disabled={total !== 100} className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition-colors ${total === 100 ? 'text-white bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-2767">
                        Confirm & Apply
                      </button>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>}
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}